import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import { getAriConfig } from '@atlassian/jira-platform-ari/src/index.tsx';
import {
	getAnalyticsTabOrder,
	getStoreEdges,
} from '@atlassian/jira-horizontal-nav-common/src/common/utils.tsx';
import type { AnalyticsTabOrder } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { TEAM_NAME } from '../../common/constants.tsx';
import type { useSetAsDefaultMutation } from './__generated__/useSetAsDefaultMutation.graphql';
import messages from './messages.tsx';

const VIEW_NOT_DEFAULTABLE_ERROR = 'View type is not defaultable';

const DEFAULT_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.setAsDefaultErrorFlagTitle,
	description: messages.setAsDefaultErrorFlagDescription,
	messageId:
		'horizontal-nav-tab-menu.services.set-as-default.default-error-flag.error.flag.default-error',
	messageType: 'transactional',
};

const getFlagDetails = (error: MutationError): FlagConfiguration | null => {
	const { message } = error;
	const { statusCode } = error.extensions || {};

	switch (statusCode) {
		case 400: {
			if (message === VIEW_NOT_DEFAULTABLE_ERROR) {
				return {
					type: 'error',
					title: messages.viewNotDefaultableErrorFlagTitle,
					description: messages.viewNotDefaultableErrorFlagDescription,
					messageId:
						'horizontal-nav-tab-menu.services.set-as-default.flag.error.view-not-defaultable-error',
					messageType: 'transactional',
				};
			}
			return null;
		}
		case 401:
			return {
				type: 'error',
				title: messages.userNotLoggedInErrorFlagTitle,
				description: messages.userNotLoggedInErrorFlagDescription,
				messageId:
					'horizontal-nav-tab-menu.services.set-as-default.flag.error.user-not-logged-in-error',
				messageType: 'transactional',
			};
		case 403:
			return {
				type: 'error',
				title: messages.userNotAdminErrorFlagTitle,
				description: messages.userNotAdminErrorFlagDescription,
				messageId:
					'horizontal-nav-tab-menu.services.set-as-default.flag.error.user-not-admin-error',
				messageType: 'transactional',
			};
		case 404:
			return {
				type: 'error',
				title: messages.viewNotFoundErrorFlagTitle,
				description: messages.viewNotFoundErrorFlagDescription,
				messageId:
					'horizontal-nav-tab-menu.services.set-as-default.flag.error.view-not-found-error',
				messageType: 'transactional',
			};
		default:
			return null;
	}
};

// exported for testing
export const PACKAGE_NAME = 'jiraNavigationKitTabMenu';
export const SET_AS_DEFAULT_VIEW = 'setAsDefaultView';

export const SET_AS_DEFAULT_EXPERIENCE = { experience: SET_AS_DEFAULT_VIEW };

export type SetNavigationItemAsDefaultArgs = {
	// values to be used in the optimistic response
	id: string;
	__id: string;
	containerNavigationId: string;
	scopeId?: string | null;
};
export const useSetAsDefault = () => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const onStartExperience = useExperienceStart({
		...SET_AS_DEFAULT_EXPERIENCE,
		analyticsSource: PACKAGE_NAME,
	});

	const onExperienceSuccess = useExperienceSuccess(SET_AS_DEFAULT_EXPERIENCE);
	const onExperienceFail = useExperienceFail(SET_AS_DEFAULT_EXPERIENCE);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [commitMutation] = useMutation<useSetAsDefaultMutation>(graphql`
		mutation useSetAsDefaultMutation($input: JiraSetDefaultNavigationItemInput!) {
			jira {
				setDefaultNavigationItem(input: $input) {
					success
					newDefault {
						label
						isDefault
					}
					previousDefault {
						isDefault
					}
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		}
	`);

	const handleError = useCallback(
		(error: MutationError | Error) => {
			showFlag(DEFAULT_ERROR_FLAG);

			fireErrorAnalytics({
				meta: {
					id: SET_AS_DEFAULT_VIEW,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			onExperienceFail(PACKAGE_NAME, error);
		},
		[onExperienceFail, showFlag],
	);

	const setAsDefault = useCallback(
		({ id, __id, containerNavigationId, scopeId }: SetNavigationItemAsDefaultArgs): void => {
			let initialTabOrder: AnalyticsTabOrder = [];
			onStartExperience();
			commitMutation({
				variables: {
					input: { id, scopeId },
				},
				optimisticUpdater: (store) => {
					initialTabOrder = getAnalyticsTabOrder(store, containerNavigationId);

					const edges = getStoreEdges(store, containerNavigationId);
					if (!edges) return;
					edges.forEach((edge) => {
						const node = edge?.getLinkedRecord('node');

						if (node?.getValue('isDefault') === true && node?.getValue('id') !== id) {
							node.setValue(false, 'isDefault');
						}

						if (node?.getValue('id') === id) {
							node?.setValue(true, 'isDefault');
						}
					});
				},
				updater: (store, response) => {
					const newDefaultRecord = store.get(__id);

					if (response?.jira?.setDefaultNavigationItem?.success) {
						if (scopeId) {
							const ariConfig = getAriConfig(scopeId);
							const scopeType = ariConfig.resourceType === 'board' ? 'JiraBoard' : 'JiraProject';
							const scopeRelayId = RelayDataID({ id: scopeId }, scopeType);
							if (scopeRelayId && newDefaultRecord) {
								const scopeRecord = store.get(scopeRelayId);
								scopeRecord?.setLinkedRecord(newDefaultRecord, 'defaultNavigationItem');
							}
						}

						const finalTabOrder = getAnalyticsTabOrder(store, containerNavigationId);
						const itemId = newDefaultRecord?.getValue('appId');

						fireTrackAnalytics(createAnalyticsEvent({}), 'projectNavigationTabItem updated', {
							originalItems: initialTabOrder,
							updatedItems: finalTabOrder,
							itemType: String(newDefaultRecord?.getValue('typeKey')).toLowerCase(),
							itemId: itemId && String(itemId).toLowerCase(),
							action: 'setAsDefault',
						});
					}
				},
				onCompleted: (result) => {
					const { success, newDefault, errors } = result?.jira?.setDefaultNavigationItem ?? {};

					if (success && newDefault && !errors) {
						showFlag({
							messageId:
								'horizontal-nav-tab-menu.services.set-as-default.show-flag.success.flag.success',
							messageType: 'transactional',
							type: 'success',
							title: formatMessage(messages.setAsDefaultSuccessFlagTitle, {
								viewName: newDefault.label,
							}),
							description: formatMessage(messages.setAsDefaultSuccessFlagDescription, {
								viewName: newDefault.label,
							}),
						});
						onExperienceSuccess();

						return;
					}

					if (errors) {
						const error = new MutationError(errors[0]);
						const statusCode = error.statusCode;

						if (statusCode != null) {
							const flagDetails = getFlagDetails(error);

							if (flagDetails) {
								showFlag(flagDetails);
								onExperienceSuccess();
								return;
							}
						}

						handleError(error);
					}
				},
				onError: (error) => handleError(error),
			});
		},
		[
			commitMutation,
			createAnalyticsEvent,
			formatMessage,
			handleError,
			onExperienceSuccess,
			onStartExperience,
			showFlag,
		],
	);

	return { setAsDefault };
};
